import { useState, Suspense, lazy, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import CreatePostModal from '../../components/CreatePostModal/CreatePostModal';
import { FeedSkeletonGrid } from '../../components/Feed';
import { ResourceCarousel } from '../../components/ResourceCarousel';
import HeaderSearchPanel from '../../components/HeaderSearchPanel';
import SubCategoryPopup from '../../components/SubCategoryPopup/SubCategoryPopup';
import ExpandedCard from '../../components/AnimalHelpGuide/ExpandedCard/ExpandedCard';
import { EngagementCarouselSkeleton } from '../../components/EngagementCarousel';
import useEngagementContent from '../../hooks/engagement/useEngagementContent';
import { handleAction } from '../../utils/searchActions';
import { getModalState, clearModalState } from '../../utils/modalStateStore';

// Lazy load FeedContainer for better initial page load
const FeedContainer = lazy(() => import('../../components/Feed/FeedContainer'));
const EngagementCarousel = lazy(() =>
  import('../../components/EngagementCarousel/EngagementCarousel')
);

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const hasAttemptedRestoration = useRef(false);

  // UI state
  const [showEmergencyTooltip, setShowEmergencyTooltip] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [createPostType, setCreatePostType] = useState(null);

  // Resource carousel state - for subcategory popup and expanded cards
  const [activeSubcategories, setActiveSubcategories] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);

  // Engagement carousel
  const engagement = useEngagementContent({ limit: 12 });

  // Check for saved modal state on mount
  useEffect(() => {
    if (hasAttemptedRestoration.current) {
      return;
    }
    hasAttemptedRestoration.current = true;
    
    const savedState = getModalState();
    
    if (savedState && savedState.currentSubcategories && savedState.currentSubcategories.length > 0) {
      setActiveSubcategories({
        subcategories: savedState.currentSubcategories,
        mainCategory: savedState.mainCategory,
        navigationHistory: savedState.navigationHistory || [],
      });
      
      clearModalState();
    }
  }, []);


  // Handle category click from carousel
  const handleCategoryClick = (category) => {
    clearModalState();
    
    handleAction(category, {
      dispatch,
      navigate,
      setActiveSubcategories,
      setExpandedCard,
    });
  };

  const handleEmergencyClick = () => {
    setShowEmergencyTooltip(true);
    setTimeout(() => setShowEmergencyTooltip(false), 3000);
  };

  const handleCreatePost = (postType) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setCreatePostType(postType);
    setShowCreatePostModal(true);
  };

  const handlePostCreated = () => {
    // Feed will auto-refetch via Apollo cache
  };

  return (
    <div className="min-h-screen relative w-full overflow-x-hidden">
      {/* Background Image */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: "url('/backgroundImages/home1.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Main Content */}
      <div
        className="relative z-10 min-h-screen flex flex-col pt-[80px] sm:pt-[92px] md:pt-[104px] w-full"
        style={{ marginTop: 'var(--account-banner-h, 0px)' }}
      >

        {/* Resource Categories Section */}
        <div className="flex-shrink-0 sm:px-4">
          <div className="max-w-4xl mx-auto">
            <div className="lg:hidden relative z-30 bg-white/95 backdrop-blur-md rounded-t-xl sm:rounded-t-2xl px-3 sm:px-4 md:px-5 pt-3 sm:pt-4 md:pt-5 pb-2 sm:pb-3">
              <HeaderSearchPanel
                isOpen={true}
                onClose={() => {}}
                variant="inline"
                fullWidth
              />
            </div>

            <div className="bg-white/95 backdrop-blur-md rounded-b-xl sm:rounded-b-2xl lg:rounded-2xl shadow-sm p-3 sm:p-4 md:p-5 overflow-hidden">
              {/* Header with Emergency Button */}
              <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                <h2 className="text-sm sm:text-base md:text-lg font-bold text-foreground leading-tight">
                  {t('find_resources', 'Find Resources Nearby')}
                </h2>
                <div className="relative flex-shrink-0">
                  <button
                    onClick={handleEmergencyClick}
                    className="px-2 sm:px-3 py-1.5 sm:py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg 
                               transition-colors text-[10px] sm:text-xs font-medium whitespace-nowrap 
                               min-h-[36px] sm:min-h-[44px] flex items-center gap-1"
                    aria-label={t('animal_emergency', 'Animal Emergency')}
                  >
                    <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{t('have_emergency', 'Have animal emergency?')}</span>
                    <span className="sm:hidden">{t('emergency', 'Emergency')}</span>
                  </button>

                  {/* Emergency Tooltip */}
                  {showEmergencyTooltip && (
                    <div className="absolute top-full right-0 mt-2 bg-white border-2 border-border 
                                    rounded-xl shadow-lg p-3 sm:p-4 w-56 sm:w-64 z-30 animate-in slide-in-from-top duration-200">
                      <p className="text-xs sm:text-sm text-foreground">
                        {t('try_ai_search', 'Try our AI search tool!')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Resource Categories Carousel */}
              <ResourceCarousel onCategoryClick={handleCategoryClick} />
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 sm:px-4 mt-2 sm:mt-3">
          <div className="max-w-4xl mx-auto">
            <Suspense fallback={<EngagementCarouselSkeleton />}>
              <EngagementCarousel
                items={engagement.items}
                loading={engagement.loading}
                refreshing={engagement.refreshing}
                error={engagement.error}
                onRetry={engagement.refetch}
              />
            </Suspense>
          </div>
        </div>

        {/* Community Feed Section */}
        <div className="flex-1 sm:px-4 mt-2 sm:mt-3">
          <div className="max-w-4xl mx-auto">
            <Suspense fallback={
              <div className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-sm overflow-hidden">
                {/* Header skeleton */}
                <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="h-5 sm:h-6 w-28 sm:w-36 bg-gray-200 rounded animate-pulse" />
                    <div className="flex gap-1.5 sm:gap-2">
                      <div className="h-9 sm:h-10 w-9 sm:w-10 bg-gray-200 rounded-lg animate-pulse" />
                      <div className="h-9 sm:h-10 w-20 sm:w-28 bg-gray-200 rounded-full animate-pulse" />
                    </div>
                  </div>
                </div>
                {/* Filter bar skeleton */}
                <div className="px-3 sm:px-4 py-2 flex gap-1.5 sm:gap-2 bg-sidebar">
                  <div className="h-8 sm:h-9 w-16 sm:w-20 bg-gray-200 rounded-full animate-pulse" />
                  <div className="h-8 sm:h-9 w-24 sm:w-28 bg-gray-200 rounded-full animate-pulse" />
                  <div className="h-8 sm:h-9 w-24 sm:w-28 bg-gray-200 rounded-full animate-pulse" />
                </div>
                {/* Feed skeleton - single column */}
                <div className="p-3 sm:p-4 bg-gray-50">
                  <FeedSkeletonGrid count={3} />
                </div>
              </div>
            }>
              <FeedContainer 
                onCreatePost={handleCreatePost}
                className="max-w-none"
              />
            </Suspense>
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreatePostModal}
        onClose={() => {
          setShowCreatePostModal(false);
          setCreatePostType(null);
        }}
        initialType={createPostType}
        onPostCreated={handlePostCreated}
      />

      {/* Subcategory Popup */}
      <AnimatePresence>
        {activeSubcategories && (
          <SubCategoryPopup
            subcategories={activeSubcategories.subcategories}
            onClose={() => setActiveSubcategories(null)}
            dispatch={dispatch}
            navigate={navigate}
            setActiveSubcategories={setActiveSubcategories}
            setExpandedCard={setExpandedCard}
            initialNavigationHistory={activeSubcategories.navigationHistory || []}
          />
        )}
      </AnimatePresence>

      {/* Expanded Card */}
      <AnimatePresence>
        {expandedCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ExpandedCard
              topic={expandedCard}
              onClose={() => setExpandedCard(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;
